import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { Button, Input, Label } from 'reactstrap';
import type { SplitCmd, CriteriaRule } from '../../../api/pickListSplit';

export interface OperatorChip { id: number; label: string; }

export interface CriteriaItem { seq: number; qty_required: number; location_code: string; ean13: string | null; }

// SP-8g §3 — "Coda comune" (pool/self-claim) rimossa: ogni sottolista va
// assegnata a operatori specifici.
// SP-8x — le BANDE (Alti/Medi volumi) accettano PIÙ operatori: i titoli
// catturati vengono distribuiti per QUANTITÀ tra loro (come auto_qty),
// producendo N missioni invece di una.
export type AssignDraft = { kind: 'operator'; operatorId: number | null };
export type AssignMultiDraft = { kind: 'operators'; operatorIds: number[] };

export interface CriteriaDraft {
  bandA: { enabled: boolean; threshold: number; assign: AssignMultiDraft };
  bandB: { enabled: boolean; min: number; max: number; assign: AssignMultiDraft };
  blocks: { enabled: boolean; n: number; orderByLocation: boolean; assign: AssignMultiDraft };
  // split-criteria-ean-atomic: il residuo accetta PIÙ operatori (distribuzione
  // per copie a titoli/EAN interi).
  residual: { orderByLocation: boolean; assign: AssignMultiDraft };
}

export const DEFAULT_CRITERIA_DRAFT: CriteriaDraft = {
  bandA: { enabled: true, threshold: 100, assign: { kind: 'operators', operatorIds: [] } },
  bandB: { enabled: true, min: 20, max: 99, assign: { kind: 'operators', operatorIds: [] } },
  blocks: { enabled: false, n: 50, orderByLocation: true, assign: { kind: 'operators', operatorIds: [] } },
  residual: { orderByLocation: true, assign: { kind: 'operators', operatorIds: [] } },
};

export interface PreviewSublist {
  label: string;
  titles: number;
  qty: number;
  locations: number;
  assignLabel: string;
}

function tt(key: string, opts?: Record<string, unknown>): string {
  return i18n.t(`picklist:${key}`, opts ?? {}) as string;
}

/**
 * SP-8x — replica frontend dell'algoritmo backend QtyBalancedDistributor:
 * quota = ceil(tot/N), scorre le righe in ordine riempiendo l'operatore
 * corrente fino alla quota (spezzando un titolo se serve). Restituisce N
 * bucket (alcuni eventualmente vuoti), ciascuno con la qta e le ubicazioni
 * dei "pezzi" assegnati. Usato per l'anteprima delle bande multi-operatore.
 */
function distributeByQty(rows: CriteriaItem[], n: number): { qty: number; locations: string[] }[] {
  const total = rows.reduce((s, r) => s + r.qty_required, 0);
  const buckets = Array.from({ length: Math.max(1, n) }, () => ({ qty: 0, locations: [] as string[] }));
  if (n < 1 || total <= 0) return buckets;
  const quota = Math.ceil(total / n);
  let cur = 0;
  for (const r of rows) {
    let remaining = r.qty_required;
    while (remaining > 0) {
      const room = quota - buckets[cur].qty;
      if (room <= 0) { cur = (cur + 1) % n; continue; }
      const take = Math.min(remaining, room);
      buckets[cur].qty += take;
      buckets[cur].locations.push(r.location_code);
      remaining -= take;
      if (buckets[cur].qty >= quota && remaining > 0) cur = (cur + 1) % n;
    }
  }
  return buckets;
}

// split-criteria-ean-atomic — raggruppa le righe per ean13 (null/'' → singoletto
// per-riga via seq). Ordina i gruppi per MIN(location) se byLocation, altrimenti
// MIN(seq); righe interne per seq. Replica di CriteriaSplitting::groupByEan.
function groupByEanFE(rows: CriteriaItem[], byLocation: boolean): CriteriaItem[][] {
  const map = new Map<string, CriteriaItem[]>();
  for (const r of rows) {
    const ean = r.ean13;
    const key = ean == null || ean === '' ? ` seq:${r.seq}` : `ean:${ean}`;
    const arr = map.get(key);
    if (arr) arr.push(r); else map.set(key, [r]);
  }
  const groups = [...map.values()].map((g) => {
    const sorted = [...g].sort((a, b) => a.seq - b.seq);
    const minLoc = sorted.reduce<string | null>((m, r) => (m === null || r.location_code < m ? r.location_code : m), null) ?? '';
    return { rows: sorted, minSeq: sorted[0].seq, minLoc };
  });
  groups.sort((a, b) => (byLocation
    ? (a.minLoc < b.minLoc ? -1 : a.minLoc > b.minLoc ? 1 : a.minSeq - b.minSeq)
    : a.minSeq - b.minSeq));
  return groups.map((x) => x.rows);
}

// split-criteria-ean-atomic — replica di EanBalancedDistributor: gruppi EAN interi,
// bilanciati per copie (LPT). Restituisce N bucket con qty/titoli/ubicazioni.
function distributeByEanBalanced(rows: CriteriaItem[], n: number): { qty: number; titles: number; locations: string[] }[] {
  const buckets = Array.from({ length: Math.max(1, n) }, () => ({ qty: 0, titles: 0, locations: [] as string[] }));
  if (n < 1 || rows.length === 0) return buckets;
  const groups = groupByEanFE(rows, false)
    .map((g) => ({ rows: g, qty: g.reduce((s, r) => s + r.qty_required, 0) }))
    .sort((a, b) => b.qty - a.qty);
  for (const g of groups) {
    let k = 0;
    for (let i = 1; i < n; i++) if (buckets[i].qty < buckets[k].qty) k = i;
    buckets[k].qty += g.qty;
    buckets[k].titles += 1;
    for (const r of g.rows) buckets[k].locations.push(r.location_code);
  }
  return buckets;
}

/** Card di banda: 1 card se 0/1 operatore, N card (distribuite per quantità) se più operatori. */
function bandCards(label: string, matched: CriteriaItem[], opIds: number[], ops: OperatorChip[]): PreviewSublist[] {
  const aggregate = (rows: CriteriaItem[], aLabel: string): PreviewSublist => ({
    label,
    titles: rows.length,
    qty: rows.reduce((s, r) => s + r.qty_required, 0),
    locations: new Set(rows.map((r) => r.location_code)).size,
    assignLabel: aLabel,
  });
  if (opIds.length <= 1) {
    const op = opIds.length === 1 ? ops.find((o) => o.id === opIds[0]) : undefined;
    return [aggregate(matched, op ? op.label : tt('wizard.criteria.assign_operator_dash'))];
  }
  const buckets = distributeByQty(matched, opIds.length);
  const out: PreviewSublist[] = [];
  buckets.forEach((bucket, i) => {
    if (bucket.qty === 0) return;
    const op = ops.find((o) => o.id === opIds[i]);
    const aLabel = op ? op.label : `#${opIds[i]}`;
    out.push({
      label: `${label} (${aLabel})`,
      titles: bucket.locations.length,
      qty: bucket.qty,
      locations: new Set(bucket.locations).size,
      assignLabel: aLabel,
    });
  });
  return out;
}

export function simulateCriteria(items: CriteriaItem[], draft: CriteriaDraft, ops: OperatorChip[]): PreviewSublist[] {
  let remaining = [...items];
  const out: PreviewSublist[] = [];

  if (draft.bandA.enabled) {
    const min = draft.bandA.threshold + 1;
    const matched = remaining.filter((r) => r.qty_required >= min);
    remaining = remaining.filter((r) => r.qty_required < min);
    if (matched.length) out.push(...bandCards(tt('wizard.criteria.sublist_labels.high_volume'), matched, draft.bandA.assign.operatorIds, ops));
  }
  if (draft.bandB.enabled) {
    const { min, max } = draft.bandB;
    const matched = remaining.filter((r) => r.qty_required >= min && r.qty_required <= max);
    remaining = remaining.filter((r) => !(r.qty_required >= min && r.qty_required <= max));
    if (matched.length) out.push(...bandCards(tt('wizard.criteria.sublist_labels.mid_volume'), matched, draft.bandB.assign.operatorIds, ops));
  }
  if (draft.blocks.enabled && remaining.length) {
    // split-criteria-ean-atomic: blocchi di N TITOLI DISTINTI (EAN); righe dello
    // stesso EAN nello stesso blocco. `titles` = titoli distinti nel blocco.
    const groups = groupByEanFE(remaining, draft.blocks.orderByLocation);
    remaining = [];
    const n = Math.max(1, draft.blocks.n);
    const blockOps = draft.blocks.assign.operatorIds;
    for (let i = 0, b = 0; i < groups.length; i += n, b += 1) {
      const blockGroups = groups.slice(i, i + n);
      const rows = blockGroups.flat();
      const op = ops.find((o) => o.id === blockOps[b % (blockOps.length || 1)]);
      const aLabel = op ? op.label : tt('wizard.criteria.assign_operator_dash');
      out.push({
        label: tt('wizard.criteria.sublist_labels.block', { n: b + 1 }),
        titles: blockGroups.length,
        qty: rows.reduce((s, r) => s + r.qty_required, 0),
        locations: new Set(rows.map((r) => r.location_code)).size,
        assignLabel: aLabel,
      });
    }
  }
  if (remaining.length) {
    // split-criteria-ean-atomic: residuo su 1 operatore = 1 card; su N operatori
    // = N card (distribuzione per copie a titoli/EAN interi).
    const opIds = draft.residual.assign.operatorIds;
    const residualLabel = tt('wizard.criteria.sublist_labels.residual');
    if (opIds.length <= 1) {
      const op = opIds.length === 1 ? ops.find((o) => o.id === opIds[0]) : undefined;
      out.push({
        label: residualLabel,
        titles: groupByEanFE(remaining, false).length,
        qty: remaining.reduce((s, r) => s + r.qty_required, 0),
        locations: new Set(remaining.map((r) => r.location_code)).size,
        assignLabel: op ? op.label : tt('wizard.criteria.assign_operator_dash'),
      });
    } else {
      distributeByEanBalanced(remaining, opIds.length).forEach((bucket, i) => {
        if (bucket.qty === 0) return;
        const op = ops.find((o) => o.id === opIds[i]);
        const aLabel = op ? op.label : `#${opIds[i]}`;
        out.push({
          label: `${residualLabel} (${aLabel})`,
          titles: bucket.titles,
          qty: bucket.qty,
          locations: new Set(bucket.locations).size,
          assignLabel: aLabel,
        });
      });
    }
  }
  return out;
}

export function buildCriteriaCmd(draft: CriteriaDraft): SplitCmd | null {
  const multi = (a: AssignMultiDraft): { operator_user_ids: number[] } | null =>
    a.operatorIds.length > 0 ? { operator_user_ids: a.operatorIds } : null;

  const criteria: CriteriaRule[] = [];
  if (draft.bandA.enabled) {
    const assign = multi(draft.bandA.assign);
    if (!assign) return null;
    criteria.push({ type: 'qty_band', min: draft.bandA.threshold + 1, max: null, assign });
  }
  if (draft.bandB.enabled) {
    const assign = multi(draft.bandB.assign);
    if (!assign) return null;
    if (draft.bandB.min > draft.bandB.max) return null;
    criteria.push({ type: 'qty_band', min: draft.bandB.min, max: draft.bandB.max, assign });
  }
  if (draft.blocks.enabled) {
    if (draft.blocks.n < 1) return null;
    const ids = draft.blocks.assign.operatorIds;
    if (ids.length === 0) return null;
    criteria.push({ type: 'every_n_titles', n: draft.blocks.n, order_by_location: draft.blocks.orderByLocation, assign: { operator_user_ids: ids } });
  }
  const residualAssign = multi(draft.residual.assign);
  if (!residualAssign) return null;
  if (criteria.length === 0) return null;
  return { mode: 'criteria', criteria, residual: { order_by_location: draft.residual.orderByLocation, assign: residualAssign } };
}

export interface CriteriaSegmentInputProps {
  items: CriteriaItem[];
  operators: OperatorChip[];
  value: CriteriaDraft;
  onChange: (next: CriteriaDraft) => void;
}

/** SP-8x — selettore MULTI operatore a chip (bande: distribuzione per quantità; blocchi: round-robin). */
function AssignMultiControl({ value, operators, onChange, label }: {
  value: AssignMultiDraft; operators: OperatorChip[]; onChange: (a: AssignMultiDraft) => void; label: string;
}) {
  return (
    <div className="d-flex align-items-center gap-2 mt-2 flex-wrap">
      <Label className="small mb-0">{label}</Label>
      {operators.map((op) => {
        const active = value.operatorIds.includes(op.id);
        return (
          <Button key={op.id} type="button" size="sm" color={active ? 'primary' : 'light'}
            aria-pressed={active}
            onClick={() => onChange({
              kind: 'operators',
              operatorIds: active ? value.operatorIds.filter((x) => x !== op.id) : [...value.operatorIds, op.id],
            })}>
            {op.label}
          </Button>
        );
      })}
    </div>
  );
}

export function CriteriaSegmentInput({ items, operators, value, onChange }: CriteriaSegmentInputProps) {
  const { t } = useTranslation('picklist');
  const set = (patch: Partial<CriteriaDraft>) => onChange({ ...value, ...patch });
  const preview = simulateCriteria(items, value, operators);

  return (
    <div className="d-flex flex-column gap-3">
      {/* Banda A */}
      <div className="border rounded p-3">
        <div className="form-check mb-1">
          <Input type="checkbox" id="critA" className="form-check-input"
            checked={value.bandA.enabled}
            onChange={(e) => set({ bandA: { ...value.bandA, enabled: e.target.checked } })} />
          <Label for="critA" className="form-check-label fw-semibold">{t('wizard.criteria.band_a_label')}</Label>
        </div>
        {value.bandA.enabled && (
          <div className="ms-4">
            <div className="d-flex align-items-center gap-2">
              <Label className="small mb-0">{t('wizard.criteria.band_a_threshold')}</Label>
              <Input type="number" min={0} style={{ width: 110 }} aria-label={t('wizard.criteria.band_a_threshold_aria')}
                value={value.bandA.threshold}
                onChange={(e) => set({ bandA: { ...value.bandA, threshold: Number(e.target.value) } })} />
            </div>
            <AssignMultiControl label={t('wizard.criteria.band_operators')} operators={operators} value={value.bandA.assign}
              onChange={(assign) => set({ bandA: { ...value.bandA, assign } })} />
          </div>
        )}
      </div>

      {/* Banda B */}
      <div className="border rounded p-3">
        <div className="form-check mb-1">
          <Input type="checkbox" id="critB" className="form-check-input"
            checked={value.bandB.enabled}
            onChange={(e) => set({ bandB: { ...value.bandB, enabled: e.target.checked } })} />
          <Label for="critB" className="form-check-label fw-semibold">{t('wizard.criteria.band_b_label')}</Label>
        </div>
        {value.bandB.enabled && (
          <div className="ms-4">
            <div className="d-flex align-items-center gap-2">
              <Label className="small mb-0">{t('wizard.criteria.band_b_min')}</Label>
              <Input type="number" min={0} style={{ width: 90 }} aria-label={t('wizard.criteria.band_b_min_aria')}
                value={value.bandB.min}
                onChange={(e) => set({ bandB: { ...value.bandB, min: Number(e.target.value) } })} />
              <Label className="small mb-0">{t('wizard.criteria.band_b_max')}</Label>
              <Input type="number" min={0} style={{ width: 90 }} aria-label={t('wizard.criteria.band_b_max_aria')}
                value={value.bandB.max}
                onChange={(e) => set({ bandB: { ...value.bandB, max: Number(e.target.value) } })} />
            </div>
            <AssignMultiControl label={t('wizard.criteria.band_operators')} operators={operators} value={value.bandB.assign}
              onChange={(assign) => set({ bandB: { ...value.bandB, assign } })} />
          </div>
        )}
      </div>

      {/* Blocchi */}
      <div className="border rounded p-3">
        <div className="form-check mb-1">
          <Input type="checkbox" id="critN" className="form-check-input"
            checked={value.blocks.enabled}
            onChange={(e) => set({ blocks: { ...value.blocks, enabled: e.target.checked } })} />
          <Label for="critN" className="form-check-label fw-semibold">{t('wizard.criteria.blocks_label')}</Label>
        </div>
        {value.blocks.enabled && (
          <div className="ms-4">
            <div className="d-flex align-items-center gap-2">
              <Label className="small mb-0">{t('wizard.criteria.blocks_n_label')}</Label>
              <Input type="number" min={1} style={{ width: 110 }} aria-label={t('wizard.criteria.blocks_n_aria')}
                value={value.blocks.n}
                onChange={(e) => set({ blocks: { ...value.blocks, n: Number(e.target.value) } })} />
            </div>
            <div className="form-check mt-2">
              <Input type="checkbox" id="critN-loc" className="form-check-input"
                checked={value.blocks.orderByLocation}
                onChange={(e) => set({ blocks: { ...value.blocks, orderByLocation: e.target.checked } })} />
              <Label for="critN-loc" className="form-check-label small">{t('wizard.criteria.blocks_order_by_location')}</Label>
            </div>
            <AssignMultiControl label={t('wizard.criteria.blocks_operators')} operators={operators} value={value.blocks.assign}
              onChange={(assign) => set({ blocks: { ...value.blocks, assign } })} />
          </div>
        )}
      </div>

      {/* Residuo */}
      <div className="border rounded p-3">
        <div className="fw-semibold mb-1">{t('wizard.criteria.residual_label')}</div>
        <div className="ms-1">
          <div className="form-check">
            <Input type="checkbox" id="critR-loc" className="form-check-input"
              checked={value.residual.orderByLocation}
              onChange={(e) => set({ residual: { ...value.residual, orderByLocation: e.target.checked } })} />
            <Label for="critR-loc" className="form-check-label small">{t('wizard.criteria.residual_order_by_location')}</Label>
          </div>
          <AssignMultiControl label={t('wizard.criteria.residual_operators')} operators={operators} value={value.residual.assign}
            onChange={(assign) => set({ residual: { ...value.residual, assign } })} />
        </div>
      </div>

      {/* Anteprima */}
      <div>
        <h4 className="fs-15 mb-2">{t('wizard.criteria.preview_title')}</h4>
        {preview.length === 0 ? (
          <p className="small text-muted mb-0">{t('wizard.criteria.preview_empty')}</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 8 }}>
            {preview.map((s, i) => (
              <div key={i} className="border rounded p-2">
                <div className="fw-semibold">{s.label}</div>
                <div className="small">{t('wizard.criteria.preview_card.titles', { titles: s.titles, qty: s.qty })}</div>
                <div className="small text-muted">{t('wizard.criteria.preview_card.locations_to', { locations: s.locations, assignee: s.assignLabel })}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
